import { CMSSection } from '@/cms/CMSSection';
import type { ButtonVariant } from '@/components/Button';
import { Button } from '@/components/Button';
import { SectionHeader } from '@/components/SectionHeader';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import type { PlaceholderType } from '@/integrations/contentful';
import { EVENT_NAME } from '@/lib/gtm/const';
import { generateDataTestId } from '@/lib/tests';
import type { WithChildren } from '@/lib/types';

import NftIcon from '@/assets/icons/nft-icon.svg';

import * as M from './messages';
import * as S from './RegisterWatchBanner.styles';

const CTA_LINK = '/';

export type RegisterWatchBannerOpts = {
  children: React.ReactNode;
  heading?: string | null;
  type: PlaceholderType;
};

type RegisterWatchBannerProps = RegisterWatchBannerOpts;

export const RegisterWatchBanner = ({
  children,
  heading,
  type,
}: RegisterWatchBannerProps) => {
  return (
    <CMSSection
      {...generateDataTestId('section', type)}
      data-test-id='RegisterWatchBannerSection'
    >
      <S.Wrapper>
        <S.InnerWrapper>
          <S.NftIcon
            alt='NtfIcon'
            data-test-id='RegisterWatchBannerNftIcon'
            height={70}
            loaderHeight={70}
            src={NftIcon}
            width={70}
          />
          <SectionHeader
            data-test-id='RegisterWatchBannerSectionHeader'
            sizeMobile={24}
            text={heading || ''}
          />
          {children}
        </S.InnerWrapper>
      </S.Wrapper>
    </CMSSection>
  );
};

const SubHeader = ({ children }: WithChildren) => {
  return (
    <S.SubHeader data-test-id='RegisterWatchBannerSubHeader'>
      {children}
    </S.SubHeader>
  );
};

const Text = ({ children }: WithChildren) => {
  return (
    <S.Description data-test-id='RegisterWatchBannerDescription'>
      {children}
    </S.Description>
  );
};

const RegisterLink = () => {
  const { sendEvent } = useGoogleAnalytics();

  return (
    <S.CTA
      data-test-id='RegisterWatchBannerCTA'
      href={CTA_LINK}
      onClick={() => sendEvent({ event: EVENT_NAME.INTENTION_REGISTER_WATCH })}
    >
      {M.RegisterLink}
      <S.Chevron data-test-id='RegisterWatchBannerChevron' />
    </S.CTA>
  );
};

export interface RegisterWatchCTAProps {
  children: React.ReactNode;
  onClick: () => void;
  variant: ButtonVariant;
}

const CTA = ({ children, onClick, variant }: RegisterWatchCTAProps) => {
  return (
    <Button
      data-test-id='RegisterWatchBannerButton'
      onClick={onClick}
      variant={variant}
    >
      {children}
    </Button>
  );
};

RegisterWatchBanner.SubHeader = SubHeader;
RegisterWatchBanner.Text = Text;
RegisterWatchBanner.RegisterLink = RegisterLink;
RegisterWatchBanner.CTA = CTA;
RegisterWatchBanner.displayName = 'RegisterWatchBanner';
